import { useState, useContext } from 'react';
import axios from 'axios';
import { useFormContext } from 'react-hook-form';
// Import contexts
import { FormDataContext } from 'globalState/FormDataContext';

const toBase64 = (str) => btoa(unescape(encodeURIComponent(str)));

const useSubmitForm = (setFormSubmitStatus) => {
  const [formDataState] = useContext(FormDataContext); // Get the state/dispatch of form data from FormDataContext
  const { trigger } = useFormContext(); // Get useForm methods
  const [isFetching, setIsFetching] = useState(false);
  const [APIErrorMessage, setAPIErrorMessage] = useState(null);

  // Destructure values from our formDataState (get all users values)

  const { EnquiryType, ContactEmail, PhoneNumber, Firstname, LastName, Report, EnquiryDetails } =
    formDataState.formData;
  // Map all destructured vals above to an object we will send to API
  let dataToSend = {};

  const emailKey = process.env.REACT_APP_EMAIL_KEY;
  let emailEnum;

  if (emailKey && emailKey.trim() !== '') {
    emailEnum = parseInt(emailKey, 10);
    // emailEnum now contains the parsed integer
  } else {
    // if the value of email is a valid integer then assign 5
    emailEnum = 3;
  }

  const emailST = 22;

  dataToSend = {
    'First Name': Firstname,
    'Last Name': LastName,
    Email: ContactEmail,
    'Phone Number': PhoneNumber,
    'Enquiry Type': EnquiryType,
    Report,
    'Enquiry Details': EnquiryDetails,
  };

  const handleSubmit = async (event) => {
    event.preventDefault(); // Prevent default form submission method
    // Validation
    const result = await trigger();

    // if no errors
    if (result) {
      // Start submitting API
      setIsFetching(true); // Set this so we can put loading state on button
      // Go hit the API with the data
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_HOST}`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: JSON.stringify({
          to: emailEnum,
          body: '{"M":"j"}',
          bodyHtml: toBase64(JSON.stringify(dataToSend)),
          from: `donotreply@tfwm.org.uk`,
          subject: `Safer Travel Contact Form: ${EnquiryType}`,
          displayName: `${Firstname} ${LastName}`,
        }),
      };

      axios
        .request(config)
        .then((response) => {
          // If the response is successful(200: OK) or error with validation message(400)
          if (response.status === 200 || response.status === 400) {
            const payload = response.config.data;

            // Log event to analytics/tag manager
            if (window && window.dataLayer) {
              window.dataLayer.push({
                event: 'formAbandonment',
                eventCategory: 'safer-travel-contact-us',
                eventAction: 'form submitted: success',
                eventLabel: 'success',
              });
            }
            setIsFetching(false); // set to false as we are done fetching now
            if (payload.Message) {
              setAPIErrorMessage(payload.Message);
            } else {
              setFormSubmitStatus(true); // Set form status to success
              window.scrollTo(0, 0); // Scroll to top of page
              // set success page
            }
            // Send a follow-up request after the initial submit.
            // Assumption: we want to notify the same API or send a confirmation/logging request after successful submission.
            // This second request is intentionally non-blocking for the user flow — failures here won't change the form status.
            try {
              const secondRequestConfig = {
                method: 'post',
                maxBodyLength: Infinity,
                url: `${process.env.REACT_APP_API_HOST}`,
                headers: {
                  'Content-Type': 'application/json',
                },
                data: JSON.stringify({
                  to: emailST,
                  body: '{"M":"j"}',
                  bodyHtml: toBase64(JSON.stringify(dataToSend)),
                  from: `donotreply@tfwm.org.uk`,
                  subject: `Safer Travel Contact Form: ${EnquiryType}`,
                  displayName: `${Firstname} ${LastName}`,
                }),
              };

              axios
                .request(secondRequestConfig)
                .then(() => {
                  // optional analytics for second request success
                  if (window && window.dataLayer) {
                    window.dataLayer.push({
                      event: 'postSubmitSecondRequest',
                      eventCategory: 'safer-travel-contact-us',
                      eventAction: 'second request success',
                    });
                  }
                })
                .catch((err) => {
                  // Log but don't change the user-facing form status
                  // eslint-disable-next-line no-console
                  console.error('Post-submit second request failed', err);
                  if (window && window.dataLayer) {
                    window.dataLayer.push({
                      event: 'postSubmitSecondRequest',
                      eventCategory: 'safer-travel-contact-us',
                      eventAction: 'second request error',
                      eventLabel: String(err),
                    });
                  }
                });
            } catch (e) {
              // If parsing or other errors occur, just log them — do not block user flow
              // eslint-disable-next-line no-console
              console.error('Error preparing post-submit request', e);
            }
            return true;
          }
          throw new Error(response.statusText, response.Message); // Else throw error and go to our catch below
        })

        // If formsubmission errors
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error({ error });
          let errMsg;

          if (error.text) {
            error.text().then((errorMessage) => {
              errMsg = errorMessage;
            });
          } else {
            errMsg = error;
          }

          // Log event to analytics/tag manager
          if (window && window.dataLayer) {
            window.dataLayer.push({
              event: 'formAbandonment',
              eventCategory: 'safer-travel-contact-us',
              eventAction: 'form submitted: error',
              eventLabel: String(errMsg),
            });
          }
          setIsFetching(false); // set to false as we are done fetching now
          setFormSubmitStatus(false); // Set form status to error
          window.scrollTo(0, 0); // Scroll to top of page
          // set error message
        });
    }
  };

  // Return handleSubmit and isFetching so it can be used by form
  return {
    handleSubmit,
    isFetching,
    APIErrorMessage,
  };
};

export default useSubmitForm;
